<template>
    <div>
        <b-row>
            <b-col xl="4" md="4" sm="6">
                <div class="form-group row">
                    <label for="title" class="col-sm-4 col-form-label label"
                        >Patient:</label
                    >
                    <div class="col" style="margin-top: 7px;">
                        <span v-if="patient">
                            <router-link
                                :to="{
                                    name: 'patient_profile',
                                    params: {
                                        id: patient.encoded_id
                                    }
                                }"
                                >{{ patient.name }}
                            </router-link>
                        </span>
                        <span
                            v-if="patient && patient.primary_owner.length > 0"
                        >
                            (<client-name
                                showPopover
                                :title="getFullName(patient.primary_owner[0])"
                                :client="patient.primary_owner[0]"
                                :text="getFullName(patient.primary_owner[0])"
                            >
                                <patient-owner-hover
                                    :owner="patient.primary_owner[0]"
                                />
                            </client-name>
                            <span v-if="patient.active_owners.length > 1">
                                +
                                <strong
                                    class="text-primary cursor-pointer"
                                    @click="$emit('load')"
                                    >{{
                                        patient.active_owners.length - 1
                                    }}</strong
                                ></span
                            >)
                        </span>
                    </div>
                </div>
                <div class="form-group row">
                    <label for="title" class="col-sm-4 col-form-label label"
                        >Title:</label
                    >
                    <div class="col">
                        <input
                            id="title"
                            name="title"
                            v-model="data.title"
                            v-validate="'required'"
                            :class="{
                                'border-danger': errors.has('title')
                            }"
                            class="form-control"
                            type="text"
                            placeholder="Title (required)"
                        />
                    </div>
                </div>
                <div class="form-group row">
                    <label for="date" class="col-sm-4 col-form-label label"
                        >Date:</label
                    >
                    <div class="col">
                        <date-picker
                            name="date"
                            :config="{
                                useCurrent: false
                            }"
                            v-model="data.date"
                            v-validate="'required'"
                            class="form-control"
                            :class="{
                                'border-danger': errors.has('date')
                            }"
                            :placeholder="getDateFormat() + ' (required)'"
                        />
                    </div>
                </div>
                <div class="form-group row">
                    <label for="recipient" class="col-sm-4 col-form-label label"
                        >Recipient:</label
                    >
                    <div class="col">
                        <vet-select
                            module="client-all-contacts"
                            placeholder="Recipient (optional)"
                            :required="false"
                            :multiple="false"
                            :showMenu="false"
                            v-model="data.recipient"
                        />
                    </div>
                </div>
                <div class="form-group row">
                    <label for="owner" class="col-sm-4 col-form-label label"
                        >Owner:</label
                    >
                    <div class="col">
                        <vue-select
                            v-model="data.owner"
                            placeholder="Select Owner (optional)"
                            :options="ownerOptions"
                            class="show-actions"
                            :clearable="true"
                        />
                    </div>
                </div>
            </b-col>
            <b-col xl="4" md="6" sm="12" style="margin-left: 16%;">
                <div class="form-group row">
                    <label for="author" class="col-sm-4 col-form-label label"
                        >Author:</label
                    >
                    <div class="col">
                        <vue-select
                            v-model="data.author"
                            label="fullname"
                            placeholder="Select author"
                            :options="authorOptions"
                            class="show-actions"
                            :clearable="false"
                        />
                    </div>
                </div>
                <div class="form-group row">
                    <label for="reference" class="col-sm-4 col-form-label label"
                        >Reference:</label
                    >
                    <div class="col">
                        <vue-select
                            v-model="data.reference"
                            label="label"
                            placeholder="Select medical note"
                            :options="medicalNoteOptions"
                            class="show-actions"
                            :clearable="false"
                        >
                            <span
                                slot="option"
                                slot-scope="option"
                                style="white-space: normal;"
                            >
                                {{ option.label }}
                            </span>
                        </vue-select>
                    </div>
                </div>
                <div class="form-group row">
                    <label
                        for="attachments"
                        class="col-sm-4 col-form-label label"
                        >Attachments:</label
                    >
                    <div class="col">
                        <span>
                            <attachment-link
                                v-for="(attachment, i) in letter.attachments"
                                :key="i"
                                :attachment="attachment"
                            ></attachment-link>
                        </span>
                    </div>
                </div>
            </b-col>
        </b-row>
    </div>
</template>
<script>
import PatientOwnerHover from "./../patient/patient-owner-hover.vue";
import PatientDocument from "@/services/patient-documents";
import vetSelect from "@/module/commons/vet-select";
import vueSelect from "vue-select";

const referenceDefaultOption = {
    label: "No Medical Note",
    id: "0"
};

export default {
    components: {
        ClientName: () => import("@/components/commons/client-name"),
        AttachmentLink: () => import("@/components/commons/attachment-link"),
        PatientOwnerHover,
        vetSelect,
        vueSelect
    },
    props: {
        letter: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            data: {
                title: this.letter.title,
                date: "",
                recipient: null,
                owner: null,
                author: null,
                reference: null
            },
            medicalNoteOptions: [],
            ownerOptions: [],
            authorOptions: [],
            name: "",
            formError: []
        };
    },
    watch: {},
    computed: {
        patient() {
            return this.letter ? this.letter.patient : "";
        },
        client() {
            return this.letter ? this.letter.client : "";
        }
    },
    mounted() {
        this.init();
    },
    methods: {
        async init() {
            let self = this;
            const owners = this.letter.patient
                ? this.letter.patient.active_owners
                : [];
            const getAuthor = PatientDocument.getAuthor();
            const getMedicalNotes = this.letter.patient
                ? PatientDocument.getPatientMedicalNote(this.patient.id)
                : [];

            const promiseResults = await Promise.all([
                getAuthor,
                getMedicalNotes
            ]);
            const [authors, rawMedicalNotes] = promiseResults;

            if (owners.length > 0) {
                this.ownerOptions = this.properOwnerList(owners);
            }
            this.medicalNoteOptions = this.properNotesList(rawMedicalNotes);
            this.authorOptions = authors;

            self.data.date = self._setDateDisplay(Number(self.letter.date));
            self.data.recipient = self.letter.recipient;
            if (self.data.recipient) {
                self.data.recipient.type = self.letter.recipient_type;
            }
            self.data.owner = self.client;
            self.letter.veterinarian.fullname = self.letter.veterinarian.label;
            self.data.author = self.letter.veterinarian;
            self.data.reference = self.letter.soap
                ? self.letter.soap
                : referenceDefaultOption;
        },
        displayProperDate(rawDate) {
            return this._toDateTime(rawDate)
                .setZone(this._timeZone)
                .toFormat(this._getLuxonFormat());
        },
        properNotesList(rawMedicalNotes = []) {
            let notes = [];

            if (rawMedicalNotes.length > 0) {
                for (const record of rawMedicalNotes) {
                    let date = this.displayProperDate(record.date);
                    let name = `${record.extended.label} (${record.type.label}) (${date})`;

                    notes.push({
                        id: record.id,
                        label: name,
                        date: record.date
                    });
                }

                notes = _.orderBy(
                    notes,
                    option => {
                        return this._toDateTime(option.date);
                    },
                    "desc"
                );
            }
            return [referenceDefaultOption, ...notes];
        },
        properOwnerList(resources = []) {
            let result = [];

            for (const record of resources) {
                result.push({
                    label: record.label,
                    id: record.id,
                    value: record.id
                });
            }

            return result;
        },
        getFullName(details) {
            return details.lastname + ", " + details.firstname;
        },
        async save() {
            let self = this;
            let recipient = "";
            let valid = await self.$validator.validateAll();
            if (!valid) {
                self.cancelSave();
                return;
            }

            if (self.data.recipient) {
                recipient = self.data.recipient.id;
                if (self.data.recipient.type == 0) {
                    recipient = "[actor]" + self.data.recipient.id;
                }
                if (self.data.recipient.type == 1) {
                    recipient = "[business]" + self.data.recipient.id;
                }
            }

            const data = {
                id: self.letter.id,
                pid: self.patient ? self.patient.id : "",
                recipient_id: recipient,
                template: self.letter.template_id,
                title: self.data.title,
                date: self.data.date,
                cid: self.data.owner ? self.data.owner.id : "",
                sid:
                    self.data.reference.id === "0"
                        ? ""
                        : self.data.reference.id,
                vet_id: self.data.author ? self.data.author.id : ""
            };

            if (typeof self.data.date === "object") {
                data.date = this.displayProperDate(self.data.date);
            }

            let res = await PatientDocument.updateLetter(data);

            if (res.message.code == 200) {
                this._message("Edits saved");
                this.$emit("edit-saved", {
                    success: true
                });
            } else {
                this._message("Error: Unable to save", "danger");
                this.$emit("edit-saved", {
                    success: false
                });
            }
        },
        cancelSave() {
            this.$emit("edit-saved", {
                success: false
            });
        }
    }
};
</script>

<style type="text/css" lang="scss">
label.col-sm-4.col-form-label.label {
    max-width: 27%;
}
</style>
